<template>
  <PgButtonActionsContext class="pg-page-edit" :actions="actions">
    <PgButtonGroup
      v-if="topMenuPosition === 'TOP'"
      class="pg-page-edit__button"
      :list="editButtons"
      :type="'text'"
      :round="false"
      :compileData="compileData"
    ></PgButtonGroup>
    <slot name="top" :formReactive="formReactive"></slot>
    <PgTopRemind
      class="pg-page-edit__remind"
      :errors="topErrors"
      :success="topSuccess"
    ></PgTopRemind>
    <Alert
      v-if="(hint?.level == 1 || hint?.level == 3) && hint?.message"
      :title="hint.message"
      :type="hintType"
      show-icon
    ></Alert>
    <div class="pg-page-edit__content">
      <slot name="left" :formReactive="formReactive"></slot>
      <PgFormTag
        class="pg-page-edit__content-form-tag"
        v-model="showTagName"
        :tagType="tagType"
        :formItems="nowFormItems"
        :labelTips="tagValidateTips"
        :model="formReactive.formData"
        :extendCompileData="extend"
        @on-complete="loadTable()"
      >
        <template #default="{ formConfig, property }">
          <PgTagForm
            class="pg-page-edit__content-form"
            :property="property"
            :onComplete="formComplete"
            :formItems="formConfig"
            :model="formReactive.formData"
            :extendCompileData="extend"
          ></PgTagForm>
        </template>
      </PgFormTag>
    </div>
    <PgButtonGroup
      v-if="topMenuPosition === 'BOTTOM'"
      class="pg-page-edit__button-bottom"
      :list="editButtons"
      :compileData="compileData"
    >
      <template #left>
        <div class="pg-page-edit__button-bottom-left"><slot name="topButton"></slot></div>
      </template>
    </PgButtonGroup>
  </PgButtonActionsContext>
</template>
<script setup>
import { computed } from 'vue'
import { Alert } from 'element-ui'
import { PgButtonActionsContext } from '../../button-actions-context/index'
import { PgFormTag } from '../../form/index'
import { PgButtonGroup } from '../../button-group/index'
import { PgTopRemind } from '../../top-remind/index'
import PgTagForm from './tag-form.vue'
import { useButtonPage, useTopErrors, useBeforValueActionsProvide } from '../../../hooks/index'
import { useForm } from '../hook/use-form'
import { getValueByPath } from '../../../utils/index'

const props = defineProps({
  formItems: {
    type: [Array, Object],
    default: null,
  },
  formItemsUrl: {
    type: String,
    default: 'rest/ezcomp/dict/form/fetch/',
  },
  formItemsCode: {
    type: String,
    default: null,
  },
  url: String,
  urlMethod: {
    type: String,
    default: 'get',
  },
  urlParamter: Object,
  menus: Array,
  topMenuPosition: {
    type: String,
    default: 'TOP', //TOP/BOTTOM
  },
  menuLead: {
    type: String,
    default: 'EDIT',
  },
  beforActions: Object,
  // 表单数据变化完成后回调
  afterCallback: Function,
  buttonActions: Object,
  extendCompileData: Object,
  modifyTip: {
    type: Boolean,
    default: true,
  },
  hintKey: {
    type: String,
    default: 'order.hint_',
  },
  // tag的样式支持card，默认无值
  tagType: String,
})

const { getButtonByKey } = useButtonPage(props.menus)
const editButtons = getButtonByKey(props.menuLead)

const { topErrors, updateErrors, topSuccess, updateSuccess } = useTopErrors()

const {
  formReactive,
  tableCompileData,
  loadTable,
  showTagName,
  tagValidateTips,
  formComplete,
  validate,
  nowFormItems,
  initLoad,
  reload,
  updateFormData,
  getAllFormData,
  setFormDataFieldVal,
  setFormModifyBy,
  addTableRows,
  removeTableRows,
  replaceRows,
  copyRows,
  copyChildRows,
} = useForm({
  formItems: props.formItems,
  formItemsUrl: props.formItemsUrl,
  formItemsCode: props.formItemsCode,
  url: props.url,
  urlMethod: props.urlMethod,
  urlParamter: props.urlParamter,
})

const hint = computed(() => getValueByPath(formReactive.formData, props.hintKey, null))
const hintType = computed(() => {
  switch (hint.value?.level) {
    case 1:
      return 'error'
    case 3:
      return 'warning'
    case 5:
      return 'info'
    case 7:
      return 'success'
    default:
      return 'info'
  }
})

// 不包含表单数据的数据源
const extend = computed(() => {
  return {
    ...props.extendCompileData,
    tableCompileData: tableCompileData.value,
    tableStaticData: formReactive.tableStaticData,
  }
})
// 包含表单数据的数据源
const compileData = computed(() => ({ data: formReactive.formData, ...extend.value }))

// 配置按钮中可调用的方法
const actions = {
  ...props.buttonActions,
  reload, //重新加载表单数据
  initLoad, //重新加载表单配置和表单数据
  validate, //校验表单
  loadTable: ({ property, isRequest }) => loadTable(property, isRequest), //渲染指定表格数据
  updateFormData: ({ data }) => updateFormData(data), //更新表单数据
  updateTopErrors: ({ errors }) => updateErrors(errors), //更新顶部错误提示
  updateSuccess: ({ data }) => updateSuccess(data), //更新顶部成功提示
  updateTopSuccess: ({ data }) => updateSuccess(data), //更新顶部成功提示
  getAllFormData, //返回当前表单整单数据
  setFormDataFieldVal: ({ field, value }) => setFormDataFieldVal(field, value), //设置表单指定字段的值
  addTableRows, //添加指定表格行数据,默认添加在最后,也支持添加到第一行，也支持添加到指定行之后{property, rows = [], aimRow, key = '_ID', unshift = false}
  removeTableRows, //删除指定表格行数据{property, ids = [], key = '_ID'}
  replaceRows, //根据key值替换表格行数据{property, rows = [], key = '_ID'}
  copyRows, //复制表格行数据{property, rows = [], key = '_ID'}
  copyChildRows, //新建表格子行数据{property, rows = [], key = '_ID'}
}

// 修改字段值前的勾子
const beforActions = {
  ...props.beforActions,
  reload,
  initLoad,
  getAllFormData,
}
useBeforValueActionsProvide({
  beforActions,
  formReactive,
  actions,
  callback: () => {
    if (props.modifyTip) {
      setFormModifyBy()
    }
  },
  afterCallback: props.afterCallback,
})

// 外部可调用的方法
defineExpose({
  validate,
  loadTable,
  reload,
  initLoad,
  updateFormData,
  getAllFormData,
  setFormDataFieldVal,
  addTableRows,
  removeTableRows, //删除指定表格行数据{property, ids = [], key = '_ID'}
  replaceRows, //根据key值替换表格行数据{property, rows = [], key = '_ID'}
  updateTopErrors: actions.updateTopErrors,
})

initLoad()
</script>
<style lang="scss">
@import '../style/page-edit.scss';
</style>
